import { ComponentPortal, DomPortalOutlet } from '@angular/cdk/portal';
import { ApplicationRef, ComponentRef, EventEmitter, Injectable, Injector, OnDestroy } from '@angular/core';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { isStorybookEnvironment } from '@sg/utils';
import { Observable, Subject } from 'rxjs';
import { SgToastComponent } from './sg-toast.component';
import { SgToastConfig, SgToastEntry, SgToastRef, SgToastType, TOAST_DURATION } from './sg-toast.model';

// Enhanced SgToastRef implementation
class SgToastRefImpl implements SgToastRef {
  private openedSubject = new Subject<void>();
  private dismissedSubject = new Subject<void>();
  private _closed = false;

  constructor(
    private toastId: string,
    private toastService: SgToastService
  ) {
    // Register this ref with the service
    this.toastService._registerRef(toastId, this);
  }

  afterOpened(): Observable<void> {
    return this.openedSubject.asObservable();
  }

  afterDismissed(): Observable<void> {
    return this.dismissedSubject.asObservable();
  }

  get closed(): boolean {
    return this._closed;
  }

  convertTo(type: SgToastType, config: Partial<SgToastConfig>): void {
    if (!this._closed) {
      this.toastService.updateToastContent(this.toastId, type, config);
    }
  }

  dismiss(): void {
    if (!this._closed) {
      this._closed = true;
      this.toastService.dismissToast(this.toastId);
      // Don't emit here - let the service handle it
    }
  }

  // Internal method called by service
  _notifyOpened(): void {
    if (!this.openedSubject.closed) {
      this.openedSubject.next();
      this.openedSubject.complete();
    }
  }

  // Internal method called by service
  _notifyDismissed(): void {
    if (!this._closed) {
      this._closed = true;
      this.dismissedSubject.next();
      this.dismissedSubject.complete();
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class SgToastService implements OnDestroy {
  //#region Properties
  allDismissed = new EventEmitter<void>();
  toastCountChanged = new EventEmitter<number>();
  //#endregion

  //#region Constants
  private readonly TOAST_HEIGHT_PX = 99;
  private readonly TOAST_CONTAINER_CLASS = 'sg-toast-container-stack';
  private readonly TOAST_WRAPPER_CLASS = 'sg-toast-wrapper';
  private readonly TOAST_WRAPPER_SUMMARY_CLASS = 'sg-toast-wrapper--summary';
  private readonly TOAST_WRAPPER_SUMMARY_STACKED_CLASS = 'sg-toast-wrapper--summary--stacked';
  private readonly TOAST_WRAPPER_INLINE_CLASS = 'sg-toast-wrapper--inline';
  private readonly TOAST_SUMMARY_SELECTOR = '.sg-toast--summary';
  private readonly TOAST_ANIMATION_DURATION_MS = 200;
  private readonly TOAST_FADE_IN_DELAY_MS = 10;
  private readonly TOAST_STACKED_ANIMATION_DELAY_MS = 180;
  private readonly TOAST_STACKED_FADE_OUT_DELAY_MS = 120;
  private readonly TOAST_Z_INDEX = '1001';
  private readonly WINDOW_HEIGHT_MARGIN_PX = 40;
  private readonly TOAST_SUMMARY_ID_PREFIX = 'summary-toast-';
  private readonly TOAST_ID_PREFIX = 'toast-';
  private readonly TOAST_WRAPPER_ID_PREFIX = 'toast-wrapper-';
  private readonly HIDE_STACKED_EFFECT_CLASS = 'hide-stacked-effect';
  //#endregion

  //#region Members
  private toastHeightPx = this.TOAST_HEIGHT_PX;
  private resizeListener: (() => void) | null = null;
  private maxVisibleToasts: number = 3;
  private toastRefs: MatSnackBarRef<SgToastComponent>[] = [];
  private toastContainer: HTMLElement | null = null;
  private portalOutlets: Map<string, DomPortalOutlet> = new Map();
  private componentRefs: Map<string, ComponentRef<SgToastComponent>> = new Map();
  private activeToastRefs: Map<string, SgToastRefImpl> = new Map();
  private inlineToastRefs: Map<string, { componentRef: ComponentRef<SgToastComponent>; element: HTMLElement }> =
    new Map();
  private activeToasts: SgToastEntry[] = [];
  private summaryToastEntry: SgToastEntry | null = null;
  private summaryToastComponentRef: ComponentRef<SgToastComponent> | null = null;
  private areToastsExpanded = false;
  //#endregion

  //#region Ctor
  constructor(
    private appRef: ApplicationRef,
    private injector: Injector
  ) {
    this.setupDynamicMaxVisibleToasts();
  }
  //#endregion

  //#region Lifecycle Hooks
  ngOnDestroy(): void {
    this.dismissAll();

    this.removeResizeListener();

    if (this.toastContainer && this.toastContainer.parentNode) {
      this.toastContainer.parentNode.removeChild(this.toastContainer);
      this.toastContainer = null;
    }

    this.summaryToastComponentRef = null;
    this.summaryToastEntry = null;
    this.toastRefs = [];
    this.activeToasts = [];
    this.portalOutlets.clear();
    this.componentRefs.clear();
    this.activeToastRefs.clear();
    this.inlineToastRefs.clear();
  }
  //#endregion

  //#region Public Methods
  show(config: SgToastConfig): SgToastRef {
    config = this.normalizeConfig(config);

    if (config.containerElement) {
      return this.createInlineToast(config);
    }

    if (isStorybookEnvironment()) {
      config = this.adjustConfigForStorybook(config);
    }

    const activeRegularToastCount = this.getActiveRegularToastCount();

    if (activeRegularToastCount === this.maxVisibleToasts && this.areToastsExpanded) {
      this.areToastsExpanded = false;
    }

    if (config.isSummaryToast) {
      return this.addToastToStack(config);
    }

    if (activeRegularToastCount >= this.maxVisibleToasts && !this.areToastsExpanded) {
      return this.handleOverflowToast(config);
    }

    if (this.summaryToastEntry && activeRegularToastCount <= this.maxVisibleToasts) {
      this.dismissSummaryToast();
    }

    return this.handleRegularToast(config);
  }

  success(config: Omit<SgToastConfig, 'type'>): SgToastRef {
    return this.show({ ...config, type: 'success' });
  }

  info(config: Omit<SgToastConfig, 'type'>): SgToastRef {
    return this.show({ ...config, type: 'info' });
  }

  warning(config: Omit<SgToastConfig, 'type'>): SgToastRef {
    return this.show({ ...config, type: 'warning' });
  }

  error(config: Omit<SgToastConfig, 'type'>): SgToastRef {
    return this.show({ ...config, type: 'error' });
  }

  wait(config: Omit<SgToastConfig, 'type'>): SgToastRef {
    return this.show({ ...config, type: 'wait', preventAutoClose: true });
  }

  dismissAll(): void {
    if (this.activeToasts.length === 0 && !this.summaryToastEntry && this.inlineToastRefs.size === 0) {
      return;
    }

    const hadToasts = this.activeToasts.length > 0 || this.inlineToastRefs.size > 0;

    // Notify all active refs that they're being dismissed
    this.activeToastRefs.forEach((ref) => {
      ref._notifyDismissed();
    });
    this.activeToastRefs.clear();

    // Dismiss all inline toasts
    this.inlineToastRefs.forEach((inlineToast) => {
      inlineToast.componentRef?.instance?.dismiss();
    });
    // Clear inline toast refs since they will be cleaned up by their dismiss handlers
    this.inlineToastRefs.clear();

    if (this.summaryToastEntry) {
      const summaryIndex = this.activeToasts.findIndex((toast) => toast.id === this.summaryToastEntry?.id);
      if (summaryIndex !== -1) {
        this.activeToasts.splice(summaryIndex, 1);
      }
      this.summaryToastEntry = null;
    }

    this.activeToasts = [];
    this.toastRefs.forEach((ref) => ref.dismiss());
    this.toastRefs = [];

    if (this.toastContainer) {
      while (this.toastContainer.firstChild) {
        this.toastContainer.removeChild(this.toastContainer.firstChild);
      }
    }

    this.componentRefs.forEach((_ref, id) => {
      if (this.portalOutlets.has(id)) {
        const outlet = this.portalOutlets.get(id);
        if (outlet) {
          try {
            outlet.detach();
            outlet.dispose();
          } catch (e) {
            // Ignore errors during cleanup
          }
        }
        this.portalOutlets.delete(id);
      }
      this.componentRefs.delete(id);
    });

    this.areToastsExpanded = false;
    this.summaryToastComponentRef = null;
    this.toastCountChanged.emit(0);
    if (hadToasts) {
      this.allDismissed.emit();
    }
  }

  /**
   * Updates the content and type of an existing toast
   */
  updateToastContent(toastId: string, newType: SgToastType, newConfig: Partial<SgToastConfig>): void {
    const componentRef = this.componentRefs.get(toastId);
    if (componentRef?.instance) {
      // Update component instance properties
      componentRef.instance.type = newType;
      componentRef.instance.title = newConfig.title || componentRef.instance.title;
      componentRef.instance.description = newConfig.description || componentRef.instance.description;
      componentRef.instance.showCloseButton = newConfig.showCloseButton ?? componentRef.instance.showCloseButton;
      componentRef.instance.preventAutoClose = newConfig.preventAutoClose ?? componentRef.instance.preventAutoClose;
      componentRef.instance.action = newConfig.action || componentRef.instance.action;

      // Update data object
      if (componentRef.instance.data) {
        componentRef.instance.data = { ...componentRef.instance.data, ...newConfig, type: newType };
      }

      // Trigger component update
      componentRef.instance.updateFromData();
      componentRef.changeDetectorRef.detectChanges();

      // Update internal tracking
      const toastEntry = this.activeToasts.find((t) => t.id === toastId);
      if (toastEntry) {
        toastEntry.config = { ...toastEntry.config, ...newConfig, type: newType };
      }
    }
  }

  /**
   * Dismisses a specific toast by ID
   */
  dismissToast(toastId: string): void {
    // Check if it's an inline toast first
    const inlineToast = this.inlineToastRefs.get(toastId);
    if (inlineToast) {
      // Trigger the component's dismiss method directly
      inlineToast.componentRef?.instance?.dismiss();
      return;
    }

    // Otherwise handle as regular toast
    this.removeToastById(toastId);
  }

  /**
   * Internal method to register a toast ref for tracking
   */
  _registerRef(toastId: string, ref: SgToastRefImpl): void {
    this.activeToastRefs.set(toastId, ref);
  }
  //#endregion

  //#region Private Methods
  private normalizeConfig(config: SgToastConfig): SgToastConfig {
    return {
      showCloseButton: true,
      showProgressBar: false,
      ...config,
    };
  }

  private adjustConfigForStorybook(config: SgToastConfig): SgToastConfig {
    const allowAutoClose = !!(window as any).__ALLOW_TOAST_AUTO_CLOSE_IN_STORYBOOK;
    return {
      ...config,
      preventAutoClose: !allowAutoClose,
    };
  }

  private getActiveRegularToastCount(): number {
    return this.activeToasts.filter((toast) => !toast.config.isSummaryToast).length;
  }

  private dismissSummaryToast(): void {
    this.summaryToastEntry.ref.dismiss();
    this.summaryToastEntry = null;
    this.updateToastsVisibility();
  }

  private handleOverflowToast(config: SgToastConfig): SgToastRef {
    const toastId = this.generateToastId();
    const toastRef = new SgToastRefImpl(toastId, this);

    if (!config.preventAutoClose) {
      const autoCloseDelay = this.getAutoCloseDuration(config);
      setTimeout(() => {
        toastRef.dismiss();
      }, autoCloseDelay);
    }

    this.activeToasts.push({
      id: toastId,
      config,
      ref: toastRef,
    });

    this.showOrUpdateSummaryToast();

    return toastRef;
  }

  private handleRegularToast(config: SgToastConfig): SgToastRef {
    const toastId = this.generateToastId();
    const toastRef = this.addToastToStack(config, toastId);

    this.activeToasts.push({
      id: toastId,
      config,
      ref: toastRef,
    });

    const newCount = this.getActiveRegularToastCount();

    if (newCount === this.maxVisibleToasts) {
      this.updateToastsVisibility();
    }

    if (newCount > this.maxVisibleToasts && !this.areToastsExpanded && !this.summaryToastEntry) {
      this.showOrUpdateSummaryToast();
    }

    this.toastCountChanged.emit(newCount);

    toastRef.afterDismissed().subscribe(() => {
      this.removeToastById(toastId);
    });

    return toastRef;
  }

  private setupDynamicMaxVisibleToasts() {
    this.calculateDynamicMaxVisibleToasts();
    this.removeResizeListener();
    this.resizeListener = () => {
      this.calculateDynamicMaxVisibleToasts();
    };
    window.addEventListener('resize', this.resizeListener);
  }

  private removeResizeListener() {
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }
  }

  private calculateDynamicMaxVisibleToasts() {
    let availableHeight = window.innerHeight;
    availableHeight = Math.max(0, availableHeight - this.WINDOW_HEIGHT_MARGIN_PX);
    const max = Math.floor(availableHeight / this.toastHeightPx);
    this.maxVisibleToasts = Math.max(1, max);
  }

  private getToastContainer(): HTMLElement {
    if (!this.toastContainer) {
      this.toastContainer = document.createElement('div');
      this.toastContainer.className = this.TOAST_CONTAINER_CLASS;
      document.body.appendChild(this.toastContainer);
    }
    return this.toastContainer;
  }

  private getAutoCloseDuration(config: SgToastConfig): number {
    if (config.action) {
      return TOAST_DURATION.WITH_ACTION;
    }

    switch (config.type) {
      case 'error':
      case 'warning':
        return TOAST_DURATION.WARNING_ERROR;
      case 'success':
      case 'info':
      default:
        return TOAST_DURATION.SUCCESS_INFO;
    }
  }

  private removeToastById(id: string): void {
    const index = this.activeToasts.findIndex((toast) => toast.id === id);
    if (index !== -1) {
      this.activeToasts.splice(index, 1);

      // Notify the ref that it's been dismissed
      const ref = this.activeToastRefs.get(id);
      if (ref) {
        ref._notifyDismissed();
        this.activeToastRefs.delete(id);
      }

      const remainingRegularToasts = this.activeToasts.filter((toast) => !toast.config.isSummaryToast).length;
      this.toastCountChanged.emit(remainingRegularToasts);

      if (this.areToastsExpanded && remainingRegularToasts === 0) {
        this.areToastsExpanded = false;
        return;
      }

      if (this.areToastsExpanded) {
        return;
      }

      if (this.summaryToastEntry) {
        this.showOrUpdateSummaryToast();
      } else if (remainingRegularToasts >= this.maxVisibleToasts && !this.areToastsExpanded) {
        this.showOrUpdateSummaryToast();
      }
    }
  }

  private showOrUpdateSummaryToast(): void {
    const regularToasts = this.activeToasts.filter((toast) => !toast.config.isSummaryToast);
    const count = regularToasts.length;

    if ((count === 0 || count < this.maxVisibleToasts) && this.summaryToastEntry) {
      this.summaryToastEntry.ref.dismiss();
      this.summaryToastEntry = null;
      this.summaryToastComponentRef = null;
      this.updateToastsVisibility();
      return;
    }

    if (count >= this.maxVisibleToasts) {
      if (!this.summaryToastEntry) {
        const summaryConfig: SgToastConfig = {
          type: 'info',
          title: `${count} Notifications`,
          description: '',
          showCloseButton: true,
          preventAutoClose: true,
          isSummaryToast: true,
          summaryCount: count,
          action: {
            text: 'Click to view all notifications',
            callback: () => this.expandToasts(),
            preventDismiss: true,
          },
        };

        if (!this.areToastsExpanded) {
          this.hideAllVisibleRegularToasts();
        }

        const summaryId = `${this.TOAST_SUMMARY_ID_PREFIX}${Date.now()}`;
        const summaryRef = this.addToastToStack(summaryConfig, summaryId);

        this.summaryToastEntry = {
          id: summaryId,
          config: summaryConfig,
          ref: summaryRef,
        };

        this.activeToasts.push(this.summaryToastEntry);

        summaryRef.afterDismissed().subscribe(() => {
          if (this.summaryToastEntry && !this.areToastsExpanded) {
            this.summaryToastEntry = null;
            this.summaryToastComponentRef = null;
            this.updateToastsVisibility();
          }
        });
      } else if (this.summaryToastComponentRef) {
        const updatedConfig: SgToastConfig = {
          ...this.summaryToastEntry.config,
          title: `${count} Notifications`,
          summaryCount: count,
        };

        this.summaryToastEntry.config = updatedConfig;
        this.summaryToastComponentRef.instance.data = updatedConfig;
        this.summaryToastComponentRef.instance.updateFromData();
      } else {
        if (this.summaryToastEntry) {
          const index = this.activeToasts.findIndex((toast) => toast.id === this.summaryToastEntry?.id);
          if (index !== -1) {
            this.activeToasts.splice(index, 1);
          }
          this.summaryToastEntry = null;
        }
        this.showOrUpdateSummaryToast();
      }
    }
  }

  private expandToasts(): void {
    this.areToastsExpanded = true;
    const summaryToastToRemove = this.summaryToastComponentRef;

    const regularToasts = this.activeToasts.filter((toast) => !toast.config.isSummaryToast);

    this.clearVisibleToasts(true);

    regularToasts.forEach((toast) => {
      const toastConfig = { ...toast.config, _isExistingToast: true };
      const visibleRef = this.addToastToStack(toastConfig);

      const index = this.activeToasts.findIndex((t) => t.id === toast.id);
      if (index !== -1) {
        const originalId = toast.id;
        const wrappedRef = new SgToastRefImpl(originalId, this);

        this.activeToasts[index].ref = wrappedRef;

        visibleRef.afterDismissed().subscribe(() => {
          this.removeToastById(originalId);
          const currentCount = this.activeToasts.filter((t) => !t.config.isSummaryToast).length;
          this.toastCountChanged.emit(currentCount);
        });
      }
    });

    if (summaryToastToRemove && this.toastContainer) {
      const wrappers = this.toastContainer.querySelectorAll(this.TOAST_SUMMARY_SELECTOR);
      wrappers.forEach((wrapper) => {
        if (wrapper.querySelector(this.TOAST_SUMMARY_SELECTOR)) {
          const htmlWrapper = wrapper as HTMLElement;
          htmlWrapper.style.opacity = '0';
          htmlWrapper.style.transform = 'translateX(20px)';

          setTimeout(() => {
            if (htmlWrapper.parentNode) {
              htmlWrapper.parentNode.removeChild(htmlWrapper);
            }
          }, this.TOAST_ANIMATION_DURATION_MS);
        }
      });
    }

    if (this.summaryToastEntry) {
      const index = this.activeToasts.findIndex((toast) => toast.id === this.summaryToastEntry?.id);
      if (index !== -1) {
        this.activeToasts.splice(index, 1);
      }
      this.summaryToastEntry = null;
      this.summaryToastComponentRef = null;
    }
  }

  private updateToastsVisibility(): void {
    const regularToasts = this.activeToasts.filter((toast) => !toast.config.isSummaryToast);
    const count = regularToasts.length;

    if (this.areToastsExpanded && count === 0) {
      this.areToastsExpanded = false;
      return;
    }

    if (this.areToastsExpanded) {
      return;
    }

    if (count <= this.maxVisibleToasts) {
      if (this.summaryToastEntry) {
        this.summaryToastEntry.ref.dismiss();
        this.summaryToastEntry = null;
      }

      this.areToastsExpanded = false;
      this.clearVisibleToasts();

      regularToasts.forEach((toast) => {
        const configCopy = { ...toast.config, _isExistingToast: true };
        const visibleRef = this.addToastToStack(configCopy);

        const index = this.activeToasts.findIndex((t) => t.id === toast.id);
        if (index !== -1) {
          const originalId = toast.id;
          const wrappedRef = new SgToastRefImpl(originalId, this);

          this.activeToasts[index].ref = wrappedRef;

          visibleRef.afterDismissed().subscribe(() => {
            this.removeToastById(originalId);
          });
        }
      });
    } else if (!this.areToastsExpanded) {
      this.clearVisibleToasts();
      this.showOrUpdateSummaryToast();
    }
  }

  private clearVisibleToasts(preserveSummaryToast: boolean = false): void {
    let summaryToast = this.summaryToastEntry;
    if (this.summaryToastEntry) {
      const index = this.activeToasts.findIndex((toast) => toast.id === this.summaryToastEntry?.id);
      if (index !== -1) {
        this.activeToasts.splice(index, 1);
      }
    }

    let summaryElement: HTMLElement | null = null;
    if (preserveSummaryToast && this.toastContainer && this.summaryToastComponentRef) {
      const wrappers = this.toastContainer.querySelectorAll(this.TOAST_SUMMARY_SELECTOR);
      wrappers.forEach((wrapper) => {
        if (wrapper.querySelector(this.TOAST_SUMMARY_SELECTOR)) {
          summaryElement = wrapper as HTMLElement;
        }
      });
    }

    if (this.toastContainer) {
      while (this.toastContainer.firstChild) {
        this.toastContainer.removeChild(this.toastContainer.firstChild);
      }
    }

    if (preserveSummaryToast && summaryElement && this.toastContainer) {
      this.toastContainer.appendChild(summaryElement);
    } else if (!preserveSummaryToast && summaryToast) {
      this.summaryToastEntry = null;
      this.summaryToastComponentRef = null;
    }
  }

  private hideAllVisibleRegularToasts(): void {
    let summaryElement = null;
    if (this.toastContainer && this.summaryToastComponentRef) {
      const wrappers = this.toastContainer.querySelectorAll(this.TOAST_SUMMARY_SELECTOR);
      wrappers.forEach((wrapper) => {
        if (wrapper.querySelector(this.TOAST_SUMMARY_SELECTOR)) {
          summaryElement = wrapper;
        }
      });
    }

    if (this.toastContainer) {
      while (this.toastContainer.firstChild) {
        this.toastContainer.removeChild(this.toastContainer.firstChild);
      }
    }

    if (summaryElement && this.toastContainer) {
      this.toastContainer.appendChild(summaryElement);
    }
  }

  private generateToastId(): string {
    return `${this.TOAST_ID_PREFIX}${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  }

  private addToastToStack(config: SgToastConfig, providedToastId?: string): SgToastRef {
    const container = this.getToastContainer();
    const toastId = providedToastId || this.generateToastId();
    const toastWrapper = document.createElement('div');
    toastWrapper.id = `${this.TOAST_WRAPPER_ID_PREFIX}${toastId}`;
    toastWrapper.className = this.TOAST_WRAPPER_CLASS;

    const isNewToast =
      !config._isExistingToast &&
      !this.activeToasts.some((t) => {
        return (
          t.config.type === config.type &&
          t.config.title === config.title &&
          t.config.description === config.description
        );
      });

    if (isNewToast) {
      toastWrapper.style.opacity = '0';
      toastWrapper.style.transform = 'translateX(20px)';
      toastWrapper.style.transition = `opacity ${this.TOAST_ANIMATION_DURATION_MS}ms ease-out, transform ${this.TOAST_ANIMATION_DURATION_MS}ms ease-out`;
    } else {
      toastWrapper.style.opacity = '1';
      toastWrapper.style.transform = 'translateX(0)';
    }

    if (container.firstChild) {
      container.insertBefore(toastWrapper, container.firstChild);
    } else {
      container.appendChild(toastWrapper);
    }

    const portalOutlet = new DomPortalOutlet(toastWrapper, this.appRef, this.injector);

    this.portalOutlets.set(toastId, portalOutlet);
    const componentPortal = new ComponentPortal(SgToastComponent);
    const componentRef = portalOutlet.attach(componentPortal);
    this.componentRefs.set(toastId, componentRef);
    if (config.isSummaryToast) {
      this.summaryToastComponentRef = componentRef;
      // Add the stacked class after the toast animates in
      setTimeout(() => {
        toastWrapper.classList.add(this.TOAST_WRAPPER_SUMMARY_STACKED_CLASS);
      }, this.TOAST_STACKED_ANIMATION_DELAY_MS); // match SCSS fade-in delay
    }

    if (componentRef?.instance) {
      componentRef.instance.data = { ...config };
      componentRef.instance.type = config.type || 'info';
      componentRef.instance.title = config.title;
      componentRef.instance.description = config.description;
      componentRef.instance.showCloseButton = config.showCloseButton !== false;
      componentRef.instance.action = config.action;
      componentRef.instance.inline = !!config.inline;
      componentRef.instance.preventAutoClose = !!config.preventAutoClose;
      componentRef.instance.showProgressBar = config.showProgressBar !== false;
    }

    componentRef.changeDetectorRef?.detectChanges();

    // Force layout recalculation to ensure proper dimensions
    // This prevents overlapping when toasts are created rapidly
    toastWrapper.offsetHeight; // Reading offsetHeight forces a reflow

    setTimeout(() => {
      if (toastWrapper.parentNode) {
        toastWrapper.style.opacity = '1';
        toastWrapper.style.transform = 'translateX(0)';

        if (config.isSummaryToast) {
          toastWrapper.style.zIndex = this.TOAST_Z_INDEX;
          toastWrapper.className = `${this.TOAST_WRAPPER_CLASS} ${this.TOAST_WRAPPER_SUMMARY_CLASS}`;
        }
      }
    }, this.TOAST_FADE_IN_DELAY_MS);

    const dismissedSubject = new Subject<void>();
    let isDismissed = false;

    // Handle hide stacked effect request
    if (config.isSummaryToast && componentRef?.instance?.hideStackedEffectRequest) {
      componentRef.instance.hideStackedEffectRequest.subscribe(() => {
        // Add class to hide stacked effect
        if (toastWrapper.classList.contains(this.TOAST_WRAPPER_SUMMARY_STACKED_CLASS)) {
          toastWrapper.classList.add(this.HIDE_STACKED_EFFECT_CLASS);

          // Wait for the stacked effect to fade out completely (120ms) before starting toast animation
          setTimeout(() => {
            // Apply the slide animation directly here
            toastWrapper.style.opacity = '0';
            toastWrapper.style.transform = 'translateX(20px)';

            // Wait for the slide animation to complete before removing
            setTimeout(() => {
              componentRef.instance.dismiss();
            }, this.TOAST_ANIMATION_DURATION_MS);
          }, this.TOAST_STACKED_FADE_OUT_DELAY_MS);
        } else {
          componentRef.instance.dismiss();
        }
      });
    }

    const toastRef = new SgToastRefImpl(toastId, this);

    // Handle dismiss event
    componentRef?.instance?.dismissed.subscribe((event) => {
      if (isDismissed) {
        return;
      }
      isDismissed = true;

      if (event?.isSummaryToast) {
        this.dismissAll();
        return;
      }

      // Only apply the animation if it hasn't been already applied by hideStackedEffectRequest
      if (toastWrapper.style.opacity !== '0') {
        toastWrapper.style.opacity = '0';
        toastWrapper.style.transform = 'translateX(20px)';
      }

      setTimeout(() => {
        if (toastWrapper.parentNode) {
          toastWrapper.parentNode.removeChild(toastWrapper);
        }

        portalOutlet.detach();
        portalOutlet.dispose();

        this.portalOutlets.delete(toastId);
        this.componentRefs.delete(toastId);

        if (config.isSummaryToast) {
          this.summaryToastComponentRef = null;
        }

        // Notify the SgToastRefImpl that it's been dismissed
        toastRef._notifyDismissed();

        // Also remove from active refs
        this.activeToastRefs.delete(toastId);

        dismissedSubject.next();
        dismissedSubject.complete();
      }, this.TOAST_ANIMATION_DURATION_MS);
    });

    // Notify that toast is opened after animation delay
    setTimeout(() => {
      toastRef._notifyOpened();
    }, this.TOAST_FADE_IN_DELAY_MS);

    return toastRef;
  }

  private createInlineToast(config: SgToastConfig): SgToastRef {
    if (!config.containerElement) {
      console.error('Container element is required for inline toasts');
      const toastId = this.generateToastId();
      const fallbackRef = new SgToastRefImpl(toastId, this);
      return fallbackRef;
    }

    const toastId = this.generateToastId();
    const wrapperDiv = document.createElement('div');
    wrapperDiv.style.position = 'relative';
    wrapperDiv.style.width = '100%';
    wrapperDiv.className = `${this.TOAST_WRAPPER_CLASS} ${this.TOAST_WRAPPER_INLINE_CLASS}`;

    config.containerElement.appendChild(wrapperDiv);

    const portalOutlet = new DomPortalOutlet(wrapperDiv, this.appRef, this.injector);
    const componentPortal = new ComponentPortal(SgToastComponent);
    const componentRef = portalOutlet.attach(componentPortal);
    if (componentRef?.instance) {
      componentRef.instance.data = { ...config, inline: true };
      componentRef.instance.inline = true;
      componentRef.instance.showCloseButton = config.showCloseButton ?? true;
      componentRef.instance.preventAutoClose = config.preventAutoClose ?? false;
      componentRef.instance.type = config.type;
      componentRef.instance.title = config.title;
      componentRef.instance.description = config.description;
      componentRef.instance.showProgressBar = config.showProgressBar ?? false;

      if (config.action) {
        componentRef.instance.action = config.action;
      }
    }
    componentRef?.changeDetectorRef?.detectChanges();

    // Track this inline toast
    this.inlineToastRefs.set(toastId, { componentRef, element: wrapperDiv });

    const toastRef = new SgToastRefImpl(toastId, this);

    const dismissedSubject = new Subject<void>();
    componentRef?.instance?.dismissed.subscribe(() => {
      wrapperDiv.style.opacity = '0';
      setTimeout(() => {
        if (wrapperDiv.parentNode) {
          wrapperDiv.parentNode.removeChild(wrapperDiv);
        }
        portalOutlet.detach();
        portalOutlet.dispose();

        // Clean up tracking
        this.inlineToastRefs.delete(toastId);

        // Notify the toast ref that it has been dismissed
        toastRef._notifyDismissed();

        dismissedSubject.next();
        dismissedSubject.complete();
      }, this.TOAST_ANIMATION_DURATION_MS);
    });

    setTimeout(() => {
      wrapperDiv.style.opacity = '1';
    }, this.TOAST_FADE_IN_DELAY_MS);

    // Notify that toast is opened after animation delay
    setTimeout(() => {
      toastRef._notifyOpened();
    }, this.TOAST_FADE_IN_DELAY_MS);

    return toastRef;
  }
  //#endregion
}
